import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useReveal } from '@/hooks/useReveal';
import { BOUTIQUE_PHOTOS } from '@/data/photos';

/**
 * Galerie 3 colonnes — défilement horizontal automatique des photos boutique,
 * sans légendes (les images parlent d'elles-mêmes).
 */
export const Gallery = () => {
    const [ref, visible] = useReveal();
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const total = BOUTIQUE_PHOTOS.length;

    useEffect(() => {
        if (paused) return undefined;
        const id = setInterval(() => setIndex((i) => (i + 1) % total), 3800);
        return () => clearInterval(id);
    }, [paused, total]);

    const go = (dir) => setIndex((i) => (i + dir + total) % total);

    // Touch swipe
    const startX = useRef(null);
    const onStart = (e) => {
        startX.current = e.touches?.[0]?.clientX ?? e.clientX;
    };
    const onEnd = (e) => {
        if (startX.current == null) return;
        const endX = e.changedTouches?.[0]?.clientX ?? e.clientX;
        const dx = endX - startX.current;
        if (Math.abs(dx) > 50) go(dx > 0 ? -1 : 1);
        startX.current = null;
    };

    return (
        <section
            id="boutique"
            ref={ref}
            data-testid="landing-gallery"
            className="relative py-20 sm:py-28 border-t border-white/5 overflow-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
                <div className={`${visible ? 'in-view' : ''} reveal flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-12`}>
                    <div>
                        <span className="section-label">01 / La boutique en images</span>
                        <h2 className="mt-4 font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
                            Bienvenue
                            <br />
                            <span className="text-outline-yellow">dans l'atelier</span>
                        </h2>
                    </div>
                    <p className="max-w-md text-base sm:text-lg text-white leading-relaxed">
                        Vitrine, showroom, rayons EPI et atelier de broderie. Bienvenue chez MANU VET, 37 avenue Charles de Gaulle, Aulnay-sous-Bois (93600).
                    </p>
                </div>
            </div>

            {/* 3-column horizontal carousel */}
            <div
                className={`${visible ? 'in-view' : ''} reveal relative`}
                style={{ animationDelay: '0.1s' }}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onTouchStart={onStart}
                onTouchEnd={onEnd}
            >
                <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
                    <div className="relative overflow-hidden corner-cut">
                        <div
                            className="flex transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] gap-3"
                            style={{
                                width: `${(total / 3) * 100}%`,
                                transform: `translateX(calc(-${index} * (100% / ${total})))`,
                            }}
                        >
                            {BOUTIQUE_PHOTOS.map((p) => (
                                <div
                                    key={p.src}
                                    className="shrink-0 corner-cut overflow-hidden border border-white/10"
                                    style={{ width: `calc((100% - ${(total - 1) * 0.75}rem) / ${total})`, aspectRatio: '4 / 5' }}
                                >
                                    <img
                                        src={p.src}
                                        alt={p.alt}
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="mt-6 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                aria-label="Précédent"
                                onClick={() => go(-1)}
                                className="h-11 w-11 inline-flex items-center justify-center bg-transparent border border-white/30 text-white hover:bg-[#FFD500] hover:text-black hover:border-[#FFD500] transition-colors corner-cut"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                aria-label="Suivant"
                                onClick={() => go(1)}
                                className="h-11 w-11 inline-flex items-center justify-center bg-[#FFD500] text-black hover:bg-white transition-colors corner-cut"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Dots / progress */}
                        <div className="flex items-center gap-1.5">
                            {BOUTIQUE_PHOTOS.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    aria-label={`Photo ${i + 1}`}
                                    onClick={() => setIndex(i)}
                                    className={`h-1.5 transition-all duration-500 ${
                                        i === index ? 'w-8 bg-[#FFD500]' : 'w-2 bg-white/25 hover:bg-white/60'
                                    }`}
                                />
                            ))}
                        </div>

                        <span className="text-xs uppercase tracking-[0.22em] text-white/70 font-bold">
                            {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};
